/**
 * Lightweight toast notification system for the sandbox editor.
 */

let container: HTMLElement | null = null;

function getContainer(): HTMLElement {
    if (!container) {
        container = document.createElement('div');
        container.className = 'toast-container';
        document.body.appendChild(container);
    }
    return container;
}

type ToastType = 'success' | 'error' | 'info';

export function showToast(message: string, type: ToastType = 'success', durationMs?: number): void {
    const duration = durationMs ?? (type === 'error' ? 8000 : 3000);

    const el = document.createElement('div');
    el.className = `toast toast-${type}`;
    el.textContent = message;

    const parent = getContainer();
    parent.appendChild(el);

    // Trigger enter animation on next frame
    requestAnimationFrame(() => el.classList.add('toast-visible'));

    setTimeout(() => {
        el.classList.remove('toast-visible');
        const remove = () => el.remove();
        el.addEventListener('transitionend', remove, { once: true });
        setTimeout(remove, 300);
    }, duration);
}

/** Dismiss function returned by `showSpinnerToast`, with an `.update()` method. */
export type SpinnerToastDismiss = ((resultMessage: string, type: ToastType) => void) & {
    /** Update the spinner message without dismissing. */
    update: (message: string) => void;
};

/**
 * Show a toast with a spinner that stays until dismissed.
 *
 * Returns a dismiss function: `dismiss(resultMessage, type)`.
 * Call `dismiss.update(msg)` to change the spinner text mid-operation.
 */
export function showSpinnerToast(message: string): SpinnerToastDismiss {
    const el = document.createElement('div');
    el.className = 'toast toast-info';
    el.innerHTML = `<span class="toast-spinner"></span><span class="toast-message">${escapeHtml(message)}</span>`;

    const parent = getContainer();
    parent.appendChild(el);
    requestAnimationFrame(() => el.classList.add('toast-visible'));

    let dismissed = false;

    const dismiss = ((resultMessage: string, type: ToastType) => {
        if (dismissed) return;
        dismissed = true;
        el.classList.remove('toast-visible');
        const remove = () => el.remove();
        el.addEventListener('transitionend', remove, { once: true });
        setTimeout(remove, 300);
        showToast(resultMessage, type);
    }) as SpinnerToastDismiss;

    dismiss.update = (newMessage: string) => {
        if (dismissed) return;
        const msgEl = el.querySelector('.toast-message');
        if (msgEl) msgEl.textContent = newMessage;
    };

    return dismiss;
}

function escapeHtml(text: string): string {
    const el = document.createElement('span');
    el.textContent = text;
    return el.innerHTML;
}
